import { Box } from '@ironhack/design-system2/components/box';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Text } from '@ironhack/design-system2/components/text';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import { type ReactElement } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { StructuredContent } from '@/app/components/client/structured-content';
import { CmsImage } from '@/app/components/server/cms-image';
import { type DatoGradientColor } from '@/lib/datocms/types';
import { type ImageAnswer } from './image-answer';

export type ImageQuestionData = {
  __typename: 'ImageQuestionRecord';
  answers: ImageAnswer[];
  description: StructuredTextGraphQlResponse;
  imageBorderColor: DatoGradientColor;
  question: string;
};

type Props = {
  color: string;
  data: Omit<ImageQuestionData, '__typename'>;
  onAnswer?: (answer: ImageAnswer) => void;
} & FlexProps;

export const ImageQuestion = (props: Props): ReactElement => {
  const {
    color,
    data: {
      answers,
      description,
      imageBorderColor: { gradientName: borderColor },
      question,
    },
    onAnswer,
    ...flexProps
  } = props;

  return (
    <ModuleWrapper
      innerProps={{
        align: 'center',
        color,
        direction: 'column',
        textAlign: 'center',
        ...flexProps,
      }}
      moduleFormat={{
        background: 'transparent',
        spacingBottom: '24',
        spacingTop: '24',
      }}
    >
      <Text as="h1" maxWidth="600px" textStyle={['3xl', null, '4xl']}>
        {question}
      </Text>
      {!isEmptyDocument(description) && (
        <StructuredContent
          data={description}
          maxWidth="600px"
          mt={1}
          textStyle="m"
        />
      )}

      <Flex columnGap={2} justify="center" mt={5} rowGap={1.5} wrap="wrap">
        {answers.map((answer: ImageAnswer) => (
          <Flex
            as="button"
            direction="column"
            gap={2}
            key={answer.code || answer.text}
            onClick={() => onAnswer?.(answer)}
            width={['156px', null, '160px']}
          >
            <Box
              background={`background.${borderColor}`}
              borderRadius="full"
              overflow="hidden"
              padding={0.5}
            >
              <CmsImage
                data={answer.image.responsiveImage}
                style={{ borderRadius: 'var(--chakra-radii-full)' }}
              />
            </Box>
            <Text textStyle="lBold">{answer.text}</Text>
          </Flex>
        ))}
      </Flex>
    </ModuleWrapper>
  );
};

export const imageQuestionFragment = `
  fragment imageQuestionFragment on ImageQuestionRecord {
    answers {
      ...imageAnswerFragment
    }
    description {
      value
    }
    imageBorderColor {
      gradientName
    }
    question
  }
`;
